import React from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { Button, IconButton } from "@mui/material";
import {
  Add,
  Delete,
  Wallpaper,
  GroupAdd,
  OpenWith,
} from "@mui/icons-material";
import {
  addElement,
  toggleBackdrop,
  updateModalManager,
  updateMultiSelection,
  updateWallsManagment,
  updateIsUserAdding,
  updateIsUserDeleting,
  updatePreviousTiles,
} from "../../../reducers/configurator/configuratorSlice";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import AddComponentDialogContent from "../addComponentDialog/AddComponentDialogContent";
import { useTranslation } from "react-i18next";
import { myExpariencesService } from "../../../services/myExpariences.service";
import { useParams } from "react-router";
import { useSnackbar } from "notistack";

const PREFIX = "TileDialogActions";

const classes = {
  root: `${PREFIX}-root`,
  marginRight: `${PREFIX}-marginRight`,
  iconButtonColor: `${PREFIX}-iconButtonColor`,
  iconButtonInsideColor: `${PREFIX}-iconButtonInsideColor`,
  adminActionsContainer: `${PREFIX}-adminActionsContainer`,
  marginLeft: `${PREFIX}-marginLeft`,
};

const TileDialogActionsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
    },
    [`& .${classes.marginLeft}`]: {
      marginLeft: "0.5rem",
    },
    [`& .${classes.marginRight}`]: {
      marginRight: "0.5rem",
    },
    [`& .${classes.iconButtonColor}`]: {
      width: "50px",
      height: "50px",
      backgroundColor: theme.palette.primary.main,
      color: "#303030",
      "&:hover > svg": {
        fill: theme.palette.primary.main,
      },
      "&:hover": {
        border: `0.5px solid ${theme.palette.primary.main}`,
        backgroundColor: "#303030",
        color: theme.palette.primary.main,
      },
    },
    [`& .${classes.adminActionsContainer}`]: {
      display: "flex",
    },
  };
});

const TileDialogActions = ({ configObjects }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const layout = useSelector((state) => state.configurator.layout);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const myTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role === "ADMIN"
  );
  const wallOptions = useSelector(
    (state) => state.configurator?.wallsManagement
  );

  const getImageFromS3 = (file) => {
    dispatch(toggleBackdrop());
    myExpariencesService
      .getImageUrlFromS3(isAdmin, id, file)
      .then((res) => {
        handleAddGroundLogoToTile(res.url);
        dispatch(toggleBackdrop());
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        dispatch(toggleBackdrop());
      });
  };

  const handleAddGroundLogoToTile = (url) => {
    dispatch(
      addElement({
        layer: "ground",
        data: {
          imageUrl: url,
          rotation: 0,
          scale: 1,
          type: "logo",
        },
        tile: currentTile,
      })
    );
  };

  const actions = {
    normal: [
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Add`}
      >
        <IconButton
          color="primary"
          aria-label="add object"
          component="span"
          variant="contained"
          className={classes.iconButtonColor}
          onClick={() =>
            dispatch(
              updateModalManager({
                open: true,
                type: "backdrop",
                renderer: <AddComponentDialogContent />,
              })
            )
          }
        >
          <Add className={classes.iconButtonInsideColor} />
        </IconButton>
      </div>,
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Add-fixed`}
      >
        <IconButton
          color="primary"
          aria-label="add fixed object"
          component="span"
          variant="contained"
          className={classes.iconButtonColor}
          onClick={() =>
            dispatch(
              updateModalManager({
                open: true,
                type: "backdrop",
                renderer: (
                  <AddComponentDialogContent configObjects={configObjects} />
                ),
              })
            )
          }
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 -960 960 960"
            width="24"
            fill="rgb(48,48,48)"
          >
            <path d="M680-119q-8 0-16-2t-15-7l-120-70q-14-8-21.5-21.5T500-249v-141q0-16 7.5-29.5T529-441l120-70q7-5 15-7t16-2q8 0 15.5 2.5T710-511l120 70q14 8 22 21.5t8 29.5v141q0 16-8 29.5T830-198l-120 70q-7 4-14.5 6.5T680-119ZM400-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM80-160v-112q0-33 17-62t47-44q51-26 115-44t141-18h14q6 0 12 2-8 18-13.5 37.5T404-360h-4q-71 0-127.5 18T180-306q-9 5-14.5 14t-5.5 20v32h252q6 21 16 41.5t22 38.5H80Zm320-400q33 0 56.5-23.5T480-640q0-33-23.5-56.5T400-720q-33 0-56.5 23.5T320-640q0 33 23.5 56.5T400-560Zm0-80Zm12 400Zm174-166 94 55 94-55-94-54-94 54Zm124 208 90-52v-110l-90 53v109Zm-150-52 90 53v-109l-90-53v109Z" />
          </svg>
        </IconButton>
      </div>,
      <div
        className={!myTile?.ground?.file ? classes.marginRight : ""}
        key={`action-${layout}-${isAdmin}-Addground`}
      >
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.psd,.tiff,.tga,.gif,.bmp,.iff,.pict"
          name={`select-ground-logo-${currentTile?.row}-${currentTile?.col}`}
          id={`select-ground-logo-${currentTile?.row}-${currentTile?.col}`}
          style={{ display: "none" }}
          onClick={(e) => (e.target.value = null)} //reinitialize the value to retrigger the onChange on the same file
          onChange={(e) => {
            getImageFromS3(e.target.files[0]);
          }}
        />
        {!myTile?.ground?.file && (
          <label
            htmlFor={`select-ground-logo-${currentTile?.row}-${currentTile?.col}`}
          >
            <IconButton
              color="primary"
              aria-label="add ground logo"
              component="span"
              variant="contained"
              className={classes.iconButtonColor}
            >
              <Wallpaper className={classes.iconButtonInsideColor} />
            </IconButton>
          </label>
        )}
      </div>,
      <div key="admin-actions" className={classes.adminActionsContainer}>
        <div
          className={classes.marginRight}
          key={`action-${layout}-${isAdmin}-gltf`}
        >
          <IconButton
            color="primary"
            aria-label="add gltf model"
            component="span"
            variant="contained"
            className={clsx(
              classes.iconButtonColor,
              classes.viewInArIconContainer
            )}
            onClick={() =>
              dispatch(
                updateModalManager({
                  open: true,
                  type: "dialog",
                  renderer: "custom",
                  mode: "displayAll",
                  title: t("configurator.3dModalTitle"),
                })
              )
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              enableBackground="new 0 0 24 24"
              height="24px"
              viewBox="0 0 24 24"
              width="24px"
              fill="#303030"
            >
              <g>
                <rect fill="none" height="24" width="24" x="0" y="0" />
              </g>
              <g>
                <g>
                  <path d="M3,4c0-0.55,0.45-1,1-1h2V1H4C2.34,1,1,2.34,1,4v2h2V4z" />
                  <path d="M3,20v-2H1v2c0,1.66,1.34,3,3,3h2v-2H4C3.45,21,3,20.55,3,20z" />
                  <path d="M20,1h-2v2h2c0.55,0,1,0.45,1,1v2h2V4C23,2.34,21.66,1,20,1z" />
                  <path d="M21,20c0,0.55-0.45,1-1,1h-2v2h2c1.66,0,3-1.34,3-3v-2h-2V20z" />
                  <path d="M19,14.87V9.13c0-0.72-0.38-1.38-1-1.73l-5-2.88c-0.31-0.18-0.65-0.27-1-0.27s-0.69,0.09-1,0.27L6,7.39 C5.38,7.75,5,8.41,5,9.13v5.74c0,0.72,0.38,1.38,1,1.73l5,2.88c0.31,0.18,0.65,0.27,1,0.27s0.69-0.09,1-0.27l5-2.88 C18.62,16.25,19,15.59,19,14.87z M11,17.17l-4-2.3v-4.63l4,2.33V17.17z M12,10.84L8.04,8.53L12,6.25l3.96,2.28L12,10.84z M17,14.87l-4,2.3v-4.6l4-2.33V14.87z" />
                </g>
              </g>
            </svg>
          </IconButton>
        </div>
        {isAdmin ? (
          <>
            <div
              className={classes.marginRight}
              key={`action-${layout}-${isAdmin}-Delete`}
            >
              <IconButton
                color="primary"
                aria-label="delete multiple"
                component="span"
                variant="contained"
                className={classes.iconButtonColor}
                onClick={() => {
                  dispatch(
                    updateMultiSelection({
                      active: true,
                      unlimited: true,
                      isDelete: true,
                      tile:
                        (myTile?.middle?.type === "arcdetriomphe" ||
                          myTile?.middle?.type === "spawn") &&
                        layout === "normal"
                          ? []
                          : [currentTile],
                      layer: "middle",
                    })
                  );
                }}
              >
                <Delete className={classes.iconButtonInsideColor} />
              </IconButton>
            </div>
            <div
              className={classes.marginRight}
              key={`action-${layout}-${isAdmin}-Add`}
            >
              <IconButton
                color="primary"
                aria-label="add guests"
                component="span"
                variant="contained"
                className={classes.iconButtonColor}
                onClick={() =>
                  dispatch(
                    updateModalManager({
                      open: true,
                      type: "dialog",
                      renderer: "guest",
                      title: t("configurator.guestTitle"),
                    })
                  )
                }
              >
                <GroupAdd className={classes.iconButtonInsideColor} />
              </IconButton>
            </div>
          </>
        ) : null}
      </div>,
      // add gltf model goes here
    ],
    event: [
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Add`}
      >
        <IconButton
          color="primary"
          aria-label="add object"
          component="span"
          variant="contained"
          className={classes.iconButtonColor}
          onClick={() => {
            dispatch(
              updateModalManager({
                open: true,
                type: "backdrop",
                renderer: <AddComponentDialogContent />,
              })
            );
            dispatch(updateIsUserAdding(true));
            dispatch(updateIsUserDeleting(false));
            dispatch(updatePreviousTiles([]));
          }}
        >
          <Add className={classes.iconButtonInsideColor} />
        </IconButton>
      </div>,
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Add-fixed`}
      >
        <IconButton
          color="primary"
          aria-label="add fixed object"
          component="span"
          variant="contained"
          className={classes.iconButtonColor}
          onClick={() => {
            dispatch(
              updateModalManager({
                open: true,
                type: "backdrop",
                renderer: (
                  <AddComponentDialogContent configObjects={configObjects} />
                ),
              })
            );
            dispatch(updateIsUserAdding(true));
            dispatch(updateIsUserDeleting(false));
            dispatch(updatePreviousTiles([]));
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 -960 960 960"
            width="24"
            fill="rgb(48,48,48)"
          >
            <path d="M680-119q-8 0-16-2t-15-7l-120-70q-14-8-21.5-21.5T500-249v-141q0-16 7.5-29.5T529-441l120-70q7-5 15-7t16-2q8 0 15.5 2.5T710-511l120 70q14 8 22 21.5t8 29.5v141q0 16-8 29.5T830-198l-120 70q-7 4-14.5 6.5T680-119ZM400-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM80-160v-112q0-33 17-62t47-44q51-26 115-44t141-18h14q6 0 12 2-8 18-13.5 37.5T404-360h-4q-71 0-127.5 18T180-306q-9 5-14.5 14t-5.5 20v32h252q6 21 16 41.5t22 38.5H80Zm320-400q33 0 56.5-23.5T480-640q0-33-23.5-56.5T400-720q-33 0-56.5 23.5T320-640q0 33 23.5 56.5T400-560Zm0-80Zm12 400Zm174-166 94 55 94-55-94-54-94 54Zm124 208 90-52v-110l-90 53v109Zm-150-52 90 53v-109l-90-53v109Z" />
          </svg>
        </IconButton>
      </div>,
      isAdmin ? (
        <div
          className={classes.marginRight}
          key={`action-${layout}-${isAdmin}-Delete`}
        >
          <IconButton
            color="primary"
            aria-label="delete multiple"
            component="span"
            variant="contained"
            className={classes.iconButtonColor}
            onClick={() => {
              dispatch(
                updateMultiSelection({
                  active: true,
                  isDelete: true,
                  tile: [currentTile],
                  unlimited: true,
                })
              );
              dispatch(updateIsUserAdding(false));
              dispatch(updateIsUserDeleting(true));
              dispatch(updatePreviousTiles([]));
            }}
          >
            <Delete className={classes.iconButtonInsideColor} />
          </IconButton>
        </div>
      ) : null,
    ],
    theme: [
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Add`}
      >
        <IconButton
          color="primary"
          aria-label="add object"
          component="span"
          variant="contained"
          className={classes.iconButtonColor}
          onClick={() => {
            dispatch(
              updateModalManager({
                open: true,
                type: "backdrop",
                renderer: <AddComponentDialogContent />,
              })
            );
          }}
        >
          <Add className={classes.iconButtonInsideColor} />
        </IconButton>
        {myTile && (myTile[layout]?.theme || myTile[layout]?.song) && (
          <IconButton
            color="primary"
            aria-label="move theme"
            component="span"
            variant="contained"
            className={clsx(classes.marginLeft, classes.iconButtonColor)}
            onClick={() =>
              dispatch(
                updateMultiSelection({
                  data: myTile[layout],
                  move: true,
                  active: true,
                  tile: currentTile,
                  oldLayer: layout,
                  layer: layout,
                })
              )
            }
          >
            <OpenWith className={classes.iconButtonInsideColor} />
          </IconButton>
        )}
      </div>,
      isAdmin ? (
        <div
          className={classes.marginRight}
          key={`action-${layout}-${isAdmin}-Delete`}
        >
          <IconButton
            color="primary"
            aria-label="delete multiple"
            component="span"
            variant="contained"
            className={classes.iconButtonColor}
            onClick={() => {
              dispatch(
                updateMultiSelection({
                  active: true,
                  isDelete: true,
                  tile: [currentTile],
                  unlimited: true,
                })
              );
              dispatch(updateIsUserAdding(false));
              dispatch(updateIsUserDeleting(true));
              dispatch(updatePreviousTiles([]));
            }}
          >
            <Delete className={classes.iconButtonInsideColor} />
          </IconButton>
        </div>
      ) : null,
    ],
    walls: [
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Add`}
      >
        <Button
          color="primary"
          aria-label="add wall"
          startIcon={<Add className={classes.iconButtonInsideColor} />}
          variant={wallOptions.mode === "add" ? "contained" : "outlined"}
          onClick={() => {
            dispatch(
              updateWallsManagment({
                ...wallOptions,
                mode: "add",
              })
            );
            dispatch(updateIsUserAdding(true));
            dispatch(updateIsUserDeleting(false));
            dispatch(updatePreviousTiles([]));
          }}
        >
          {t("configurator.addWall")}
        </Button>
      </div>,
      <div
        className={classes.marginRight}
        key={`action-${layout}-${isAdmin}-Delete`}
      >
        <Button
          color="primary"
          aria-label="delete walls"
          startIcon={<Delete className={classes.iconButtonInsideColor} />}
          variant={wallOptions.mode === "delete" ? "contained" : "outlined"}
          onClick={() => {
            dispatch(
              updateWallsManagment({
                ...wallOptions,
                mode: "delete",
              })
            );
            dispatch(updateIsUserDeleting(true));
            dispatch(updateIsUserAdding(false));
            dispatch(updatePreviousTiles([]));
          }}
        >
          {t("configurator.deleteWall")}
        </Button>
      </div>,
    ],
  };

  return (
    <TileDialogActionsContainer>
      <div className={classes.root}>{actions[layout]}</div>
    </TileDialogActionsContainer>
  );
};

export default TileDialogActions;
